import {
  CalendarBlank,
  ChartLine,
  ClockCounterClockwise,
  GlobeHemisphereWest,
} from '@phosphor-icons/react'
import { useContext, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useHistory, useParams } from 'react-router-dom'

import usePermissions from '@/hooks/usePermissions'
import { useLoadCustomer } from '@/queries/customers/useLoadCustomer'
import Ui from '@/ui'
import { ZentakeContext } from '@/utils/ZentakeContext'

import ClientDemographicsTable from './ClientDemographics/ClientDemographicsTable'
import { ClientHeader } from './ClientHeader/ClientHeader'
import { MeasuresView } from './ClientMeasures/MeasuresView'
import ClientResponsesTable from './ClientResponsesTable'
import Routines from './Routines'

type Props = {
  selectedTab: string
}
const ClientDetailsContentTabs = ({ selectedTab }: Props) => {
  switch (selectedTab) {
    case 'timeline':
      return <ClientResponsesTable loading={false} />
    case 'demographics':
      return <ClientDemographicsTable />
    case 'routines':
      return <Routines />
    case 'measures':
      return <MeasuresView />
    default:
      return <div>Tab not implemented</div>
  }
}

export const ClientDetailsContent = () => {
  const { clientId } = useParams<{ clientId: string }>()
  const history = useHistory()
  const { canViewResponse } = usePermissions()
  const { teamDetails } = useContext(ZentakeContext)
  const { data: clientDetail } = useLoadCustomer(clientId, {
    enabled: !!clientId,
  })

  const onSelectOption = ({ id }: { id: number }) => history.replace(`#${id}`)

  const currentPage = window.location.hash.replace('#', '') || 'timeline'

  const allowedTabs = useMemo(() => {
    const tabs = []

    if (canViewResponse) {
      tabs.push({
        id: 'timeline',
        title: (
          <>
            <ClockCounterClockwise className="text-base" />
            Timeline
          </>
        ),
      })
    }

    tabs.push({
      id: 'demographics',
      title: (
        <>
          <GlobeHemisphereWest className="text-base" /> Demographics
        </>
      ),
    })

    if (teamDetails?.scheduled_forms === true) {
      tabs.push({
        id: 'routines',
        title: (
          <>
            <CalendarBlank className="text-base" /> Scheduled Forms
          </>
        ),
      })
    }

    if (teamDetails?.measures) {
      tabs.push({
        id: 'measures',
        title: (
          <>
            <ChartLine className="text-base" /> Measures
          </>
        ),
      })
    }
    return tabs
  }, [canViewResponse, teamDetails?.measures, teamDetails?.scheduled_forms])

  const currentSelectedTabs = allowedTabs.findIndex(
    (item) => item.id === currentPage
  )

  const fullName = [clientDetail?.first_name, clientDetail?.last_name]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      <Helmet>
        <title>{fullName} - Client/Zentake</title>
      </Helmet>

      <ClientHeader />
      <div className="mx-auto mt-6 max-w-screen-xl pb-11 md:px-3">
        <div>
          <Ui.Tabs
            initialSelectedIndex={currentSelectedTabs}
            onSelectOption={onSelectOption}
            options={allowedTabs}
          />
          <ClientDetailsContentTabs selectedTab={currentPage} />
        </div>
      </div>
    </>
  )
}
